/**
 * 对话配置编辑器
 * 支持对话树、NPC对话、触发条件等配置
 */
import React, { useState, useEffect } from 'react'
import {
  Card,
  Form,
  Input,
  Select,
  InputNumber,
  Button,
  Space,
  Tabs,
  Tag,
  Row,
  Col,
  Typography,
  Table,
  Switch,
  Divider,
  message,
} from 'antd'
import {
  PlusOutlined,
  DeleteOutlined,
  MessageOutlined,
  TeamOutlined,
  BranchesOutlined,
} from '@ant-design/icons'
import type { DialogueConfigData, DialogueEntry, DialogueLine } from '@/types/scriptConfigBlock'

const { TextArea } = Input
const { Text, Title } = Typography

// 触发类型选项
const TRIGGER_TYPE_OPTIONS = [
  { value: 'proximity', label: '靠近触发' },
  { value: 'talk', label: '主动对话' },
  { value: 'quest_start', label: '任务开始' },
  { value: 'quest_complete', label: '任务完成' },
  { value: 'variable', label: '变量触发' },
  { value: 'time', label: '时间触发' },
]

// 对话类型选项
const DIALOGUE_TYPE_OPTIONS = [
  { value: 'linear', label: '线性对话' },
  { value: 'branching', label: '分支对话' },
  { value: 'conditional', label: '条件对话' },
]

interface DialogueEditorFormProps {
  initialData?: DialogueConfigData
  onSave: (data: DialogueConfigData) => Promise<void>
  loading?: boolean
}

// 对话条目编辑器
const DialogueEntryEditor: React.FC<{
  entry: DialogueEntry
  index: number
  onUpdate: (index: number, entry: DialogueEntry) => void
  onRemove: (index: number) => void
}> = ({ entry, index, onUpdate, onRemove }) => {
  return (
    <Card size="small" style={{ marginBottom: 16 }}>
      <Row gutter={16}>
        <Col span={8}>
          <Input
            value={entry.id}
            onChange={(e) => onUpdate(index, { ...entry, id: e.target.value })}
            placeholder="对话ID"
          />
        </Col>
        <Col span={8}>
          <Input
            value={entry.trigger}
            onChange={(e) => onUpdate(index, { ...entry, trigger: e.target.value })}
            placeholder="触发条件"
          />
        </Col>
        <Col span={8}>
          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => onRemove(index)}>
            删除
          </Button>
        </Col>
      </Row>
      <Divider style={{ margin: '12px 0' }} />
      <Text type="secondary">
        对话内容：{entry.lines?.length || 0} 条
      </Text>
    </Card>
  )
}

// 主组件
const DialogueEditorForm: React.FC<DialogueEditorFormProps> = ({
  initialData,
  onSave,
  loading = false,
}) => {
  const [form] = Form.useForm()
  const [activeTab, setActiveTab] = useState('basic')
  const [dialogues, setDialogues] = useState<DialogueEntry[]>([])
  const [saving, setSaving] = useState(false)

  // 初始化表单数据
  useEffect(() => {
    if (initialData) {
      form.setFieldsValue({
        key: initialData.key,
        name: initialData.name,
        description: initialData.description,
        participants: initialData.participants?.join(', '),
      })
      setDialogues(initialData.dialogues || [])
    } else {
      form.resetFields()
      setDialogues([])
    }
  }, [initialData, form])

  // 保存处理
  const handleSave = async () => {
    try {
      const values = await form.validateFields()
      setSaving(true)

      const dialogueData: DialogueConfigData = {
        key: values.key,
        name: values.name,
        description: values.description,
        participants: values.participants
          ? values.participants.split(',').map((p: string) => p.trim())
          : [],
        dialogues,
      }

      await onSave(dialogueData)
    } catch (error) {
      console.error('表单验证失败:', error)
    } finally {
      setSaving(false)
    }
  }

  // 添加对话条目
  const addDialogue = () => {
    const newDialogue: DialogueEntry = {
      id: `dialogue_${Date.now()}`,
      trigger: '',
      lines: [],
    }
    setDialogues([...dialogues, newDialogue])
  }

  // 更新对话条目
  const updateDialogue = (index: number, entry: DialogueEntry) => {
    const newDialogues = [...dialogues]
    newDialogues[index] = entry
    setDialogues(newDialogues)
  }

  // 删除对话条目
  const removeDialogue = (index: number) => {
    setDialogues(dialogues.filter((_, i) => i !== index))
  }

  return (
    <Card
      title={
        <Space>
          <MessageOutlined style={{ color: '#52c41a' }} />
          <span>{initialData?.name || '新建对话'}</span>
          {initialData?.key && <Text code style={{ fontSize: 12 }}>{initialData.key}</Text>}
        </Space>
      }
      extra={
        <Button
          type="primary"
          icon={<TeamOutlined />}
          onClick={handleSave}
          loading={saving || loading}
        >
          保存
        </Button>
      }
    >
      <Form form={form} layout="vertical">
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="基本信息" key="basic">
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="key" label="对话键" rules={[{ required: true }]}>
                  <Input placeholder="例如: dialogue_intro" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="name" label="对话名称" rules={[{ required: true }]}>
                  <Input placeholder="例如: 新手引导对话" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="dialogue_type" label="对话类型">
                  <Select placeholder="选择对话类型" options={DIALOGUE_TYPE_OPTIONS} />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="description" label="描述">
              <TextArea rows={2} placeholder="对话的用途说明..." />
            </Form.Item>

            <Form.Item name="participants" label="参与者 (逗号分隔)">
              <Input placeholder="npc_mysterious_man, npc_old_mo" />
            </Form.Item>
          </TabPane>

          <TabPane tab={`对话条目 (${dialogues.length})`} key="entries">
            <div style={{ marginBottom: 16 }}>
              <Button type="dashed" icon={<PlusOutlined />} onClick={addDialogue}>
                添加对话条目
              </Button>
            </div>

            {dialogues.map((entry, index) => (
              <DialogueEntryEditor
                key={index}
                entry={entry}
                index={index}
                onUpdate={updateDialogue}
                onRemove={removeDialogue}
              />
            ))}

            {dialogues.length === 0 && (
              <Card size="small" style={{ textAlign: 'center' }}>
                <Text type="secondary">暂无对话条目，点击上方按钮添加</Text>
              </Card>
            )}
          </TabPane>

          <TabPane tab="开始条件" key="conditions">
            <Card size="small">
              <Form.Item name="start_conditions" label="开始条件 (JSON)">
                <TextArea
                  rows={6}
                  placeholder={`{
  "proximity_distance": 3,
  "player_inventory_contains": "item_ticket",
  "or": [
    {"condition": "sw_meet_stranger"},
    {"condition": "sw_quest_started"}
  ]
}`}
                  style={{ fontFamily: 'monospace' }}
                />
              </Form.Item>
            </Card>

            <Divider />

            <Card size="small" title="结束条件">
              <Form.Item name="end_conditions" label="结束条件 (JSON)">
                <TextArea
                  rows={6}
                  placeholder={`{
  "all_lines_completed": true,
  "or": [
    {"condition": "sw_evidence_collected"},
    {"condition": "sw_dialogue_skipped"}
  ]
}`}
                  style={{ fontFamily: 'monospace' }}
                />
              </Form.Item>
            </Card>
          </TabPane>

          <TabPane tab="模板参考" key="templates">
            <Row gutter={16}>
              <Col span={12}>
                <Card size="small" title="分支对话示例">
                  <pre style={{ margin: 0, fontSize: 12 }}>
{`{
  "id": "branch_dialogue",
  "type": "branching",
  "branches": [
    {
      "condition": "var_evidence >= 3",
      "lines": [...],
      "next": "branch_evidence"
    },
    {
      "condition": "default",
      "lines": [...],
      "next": "branch_normal"
    }
  ]
}`}
                  </pre>
                </Card>
              </Col>
              <Col span={12}>
                <Card size="small" title="条件对话示例">
                  <pre style={{ margin: 0, fontSize: 12 }}>
{`{
  "id": "conditional_dialogue",
  "type": "conditional",
  "conditions": [
    {
      "condition": "faction_reputation >= 50",
      "content": "欢迎加入我们！"
    },
    {
      "condition": "faction_reputation < 0",
      "content": "你是敌人！"
    }
  ]
}`}
                  </pre>
                </Card>
              </Col>
            </Row>
          </TabPane>
        </Tabs>
      </Form>
    </Card>
  )
}

const { TabPane } = Tabs

export default DialogueEditorForm
